import { FC, memo, useCallback, useState } from 'react';
import { useBotContext } from '../useBotContext';
import BotAHUSection from '../BotAHUSection';
import Button from '../atom/Button';
import {
  createBotSection,
  canRemoveFanSection,
  applyFanSectionRules,
  applyOtherSectionRules,
  applyAddReturnAirflowRules,
  applyAddOutsideAirflowRules,
  checkIsMixedAirPlenum,
} from '../../helpers/data/bot';
import {
  BotTunnelProps,
  IBotData,
  IBotSection,
  MAX_SECTIONS_PER_TUNNEL,
} from '../../models';
import './styles.scss';
import Tooltip from '../atom/Tooltip';

const BotTunnel: FC<BotTunnelProps> = ({
  tunnel,
  tIndex,
  onSelectSection,
  onToggleAccessories,
  selected,
}) => {
  const { setValue, value: botData } = useBotContext();

  const sections: IBotSection[] = tunnel.sections ?? [];

  const handleAddSection = useCallback(() => {
    if (sections.length >= MAX_SECTIONS_PER_TUNNEL) return;

    setValue((draft: IBotData) => {
      const targetTunnel = draft.tunnels[tIndex];
      if (
        !targetTunnel ||
        targetTunnel.sections.length >= MAX_SECTIONS_PER_TUNNEL
      ) {
        return;
      }

      const newSection = createBotSection({
        airflow: targetTunnel.airflow,
        air_stream_id: targetTunnel.air_stream_id,
      });
      targetTunnel.sections.push(newSection);
      onSelectSection?.(tIndex, targetTunnel.sections.length - 1);
    });
  }, [sections.length, setValue, tIndex, onSelectSection]);

  // Native HTML5 drag-and-drop stand-in for @hello-pangea/dnd (not a project
  // dependency here) — same single-list vertical reorder, driven from the
  // drag-handle icon only (draggable is set on the handle, not the <li>).
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragEnd = useCallback(
    (sourceIndex: number, destIndex: number) => {
      if (sourceIndex === destIndex) return;

      // 🚫 Block moving any section into index 0
      // when section[0] is protected (Mixed Air Plenum case)
      if (destIndex === 0 && botData.air_system_id === 2) {
        return;
      }

      setValue((draft: IBotData) => {
        const targetTunnel = draft.tunnels[tIndex];
        if (!targetTunnel) return;

        const [movedSection] = targetTunnel.sections.splice(sourceIndex, 1);
        targetTunnel.sections.splice(destIndex, 0, movedSection);
      });
    },
    [setValue, tIndex, botData.air_system_id],
  );

  const handleRemoveSection = useCallback(
    (_tunnelIndex: number, sectionIndex: number) => {
      /**
       * Removes a section from the tunnel with comprehensive rule validation
       *
       * Business Rules:
       * 1. er_wheel sections:
       *    - Cannot remove from tunnel[1] if it exists in tunnel[0]
       *    - When removed from tunnel[0], also removes from tunnel[1]
       * 2. fan sections:
       *    - Removal validated by canRemoveFanSection() based on config
       * 3. Other sections: Can always be removed
       *
       * After removal, applies configuration rules to maintain valid state
       */
      setValue((draft: IBotData) => {
        const targetTunnel = draft.tunnels[tIndex];
        if (!targetTunnel) return;

        const sectionToRemove = targetTunnel.sections[sectionIndex];

        // Prevent removal of er_wheel from tunnel 1 if it exists in tunnel 0
        if (
          sectionToRemove?.type === 'er_wheel' &&
          tIndex === 1 &&
          draft.tunnels.length > 0
        ) {
          const tunnel0 = draft.tunnels[0];
          if (tunnel0?.sections.some((s) => s.type === 'er_wheel')) {
            return; // Don't remove it
          }
        }

        // Check if fan section can be removed based on configuration rules
        if (
          sectionToRemove?.type === 'fan' &&
          !canRemoveFanSection(draft, tIndex, sectionIndex)
        ) {
          return; // Block removal
        }

        // When Rotary Htex (er_wheel) is removed from first tunnel (tIndex 0),
        // also remove it from the second tunnel (tIndex 1)
        if (
          sectionToRemove?.type === 'er_wheel' &&
          tIndex === 0 &&
          draft.tunnels.length > 1
        ) {
          const tunnel1 = draft.tunnels[1];
          if (tunnel1) {
            const erWheelIndex = tunnel1.sections.findIndex(
              (s) => s.type === 'er_wheel',
            );
            if (erWheelIndex !== -1) {
              tunnel1.sections.splice(erWheelIndex, 1);
            }
          }
        }

        targetTunnel.sections.splice(sectionIndex, 1);
        // Apply fan section rules after removal
        applyOtherSectionRules(draft);
        applyFanSectionRules(draft);
        applyAddReturnAirflowRules(draft);
        applyAddOutsideAirflowRules(draft);
      });
    },
    [setValue, tIndex],
  );

  const handleAccessoriesClick = (
    e: React.MouseEvent,
    tIndex: number,
    sIndex: number,
  ) => {
    onSelectSection?.(tIndex, sIndex);
    const target = e.currentTarget.closest('.section-item') as HTMLLIElement;
    if (target) {
      const rect = target.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const top = rect.top + scrollTop;
      onToggleAccessories?.(e, top);
    }
  };

  // Check if a specific section can be deleted
  const canDeleteSection = useCallback(
    (sectionIndex: number) => {
      const section = sections[sectionIndex];
      if (!section) return false;

      // Check if it's Mixed Air Plenum (protected)
      if (checkIsMixedAirPlenum(sectionIndex, botData.air_system_id)) {
        return false;
      }

      // Check if it's a protected er_wheel
      if (section.type === 'er_wheel') {
        return false;
      }

      // Check if it's a protected fan
      if (
        section.type === 'fan' &&
        !canRemoveFanSection(botData, tIndex, sectionIndex)
      ) {
        return false;
      }

      return true;
    },
    [sections, botData, tIndex],
  );

  return (
    <div className="bot-tunnel">
      <div className="d-flex justify-content-between">
        Tunnel {tunnel.tunnel_index ?? '1:1'} : Sections
        <div className="bot-tunnel__buttons">
          <Button
            type="button"
            className="has-plus-icon add-section-btn bg-transparent"
            onClick={handleAddSection}
            primary
            hasShadow={false}
            tabIndex={-1}
            disabled={sections.length === MAX_SECTIONS_PER_TUNNEL}
          >
            <Tooltip placement="top" title="Add Section">
              <span className="add-btn">ADD</span>
            </Tooltip>
          </Button>
        </div>
      </div>
      <div className="bot-tunnel__sections">
        <ul className="sections-list">
          {sections.map((section, sIndex) => {
            const isSelected = selected === sIndex;
            const isDragDisabled = checkIsMixedAirPlenum(
              sIndex,
              botData.air_system_id,
            );
            return (
              <li
                key={section.id}
                className={`section-item ${
                  isSelected ? 'is-selected' : ''
                } ${section.error ? 'has_error' : ''} ${
                  draggingIndex === sIndex ? 'is-dragging' : ''
                } ${dragOverIndex === sIndex ? 'is-drag-over' : ''}`}
                onDragOver={(e) => {
                  if (draggingIndex === null) return;
                  e.preventDefault();
                  if (dragOverIndex !== sIndex) setDragOverIndex(sIndex);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggingIndex !== null) {
                    handleDragEnd(draggingIndex, sIndex);
                  }
                  setDraggingIndex(null);
                  setDragOverIndex(null);
                }}
              >
                <div
                  className="section-item__drag-handle"
                  draggable={!isDragDisabled}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    try {
                      e.dataTransfer.setData('text/plain', String(section.id));
                    } catch {
                      /* noop */
                    }
                    setDraggingIndex(sIndex);
                  }}
                  onDragEnd={() => {
                    setDraggingIndex(null);
                    setDragOverIndex(null);
                  }}
                  aria-label="Reorder section"
                  onClick={onSelectSection?.bind(null, tIndex, sIndex)}
                  tabIndex={-1}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    &#xe8fe;
                  </span>
                </div>
                <div className="section-item__content">
                  <BotAHUSection
                    tunnelIndex={tIndex}
                    sectionIndex={sIndex}
                    section={section}
                    onSelect={onSelectSection}
                    isSelected={isSelected}
                  />
                  <div
                    className="empty-part"
                    aria-label="Reorder section"
                    onClick={onSelectSection?.bind(null, tIndex, sIndex)}
                    tabIndex={-1}
                  />
                </div>
                {section.error && (
                  <div className="section-item-error">
                    <Tooltip title={section.error} placement="top">
                      <span>error...</span>
                    </Tooltip>
                  </div>
                )}

                <Button
                  type="button"
                  className="section-item__accessories-btn accessories-toggle-btn"
                  onClick={(e) => handleAccessoriesClick(e, tIndex, sIndex)}
                  aria-label="Toggle accessories"
                  tabIndex={-1}
                  hasShadow={false}
                  primary
                >
                  Accessories...
                </Button>
                <Button
                  type="button"
                  className="section-item__delete-btn bg-transparent"
                  aria-label="Delete section"
                  onClick={() => handleRemoveSection(tIndex, sIndex)}
                  disabled={!canDeleteSection(sIndex)}
                  tabIndex={-1}
                  hasShadow={false}
                >
                  <Tooltip placement="top" title="Delete Section">
                    <span className="icon-trash-o" aria-hidden></span>
                  </Tooltip>
                </Button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default memo(BotTunnel);
